import { useNavigate } from 'react-router-dom'
import {
  Bot, CheckCircle2, AlertCircle, Play, DollarSign, Clock,
  FileText, BookOpen, ShoppingCart, Megaphone, Image, HelpCircle,
  GitBranch, Layers, Upload
} from 'lucide-react'
import { useApp } from '../context/AppContext'
import { formatDateShort } from '../utils/helpers'

const StatCard = ({ icon: Icon, label, value, subtext, color = 'primary' }) => {
  const colorMap = {
    primary: 'bg-primary-50 text-primary-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  }
  return (
    <div className="card p-5 card-hover">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[color] || colorMap.primary}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  )
}

const WorkbenchCard = ({ icon: Icon, title, desc, path, color }) => {
  const navigate = useNavigate()
  const colorMap = {
    purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 hover:bg-orange-100 border-orange-200',
    pink: 'bg-pink-50 text-pink-600 hover:bg-pink-100 border-pink-200',
    cyan: 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100 border-cyan-200',
  }
  return (
    <div onClick={() => navigate(path)} className={`p-5 rounded-xl border cursor-pointer transition-all ${colorMap[color]}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon size={24} />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="text-sm opacity-80">{desc}</p>
    </div>
  )
}

export default function Dashboard() {
  const { stats, agents, prompts, knowledgeBases } = useApp()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Dashboard 首页</h2>
        <span className="text-sm text-slate-500">{new Date().toLocaleDateString('zh-CN')}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Bot} label="Agent 总数" value={stats.totalAgents} subtext={`单Agent ${stats.singleAgents} / 工作流 ${stats.workflowAgents}`} color="primary" />
        <StatCard icon={GitBranch} label="已迁移 Agent" value={stats.migratedAgents} subtext={`迁移率 ${Math.round((stats.migratedAgents / stats.totalAgents) * 100)}%`} color="emerald" />
        <StatCard icon={Play} label="今日执行次数" value={stats.todayExecutions} subtext="较昨日 +3" color="amber" />
        <StatCard icon={DollarSign} label="本月模型成本" value={`$${stats.monthlyCost}`} subtext="预算剩余 $152.5" color="purple" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={AlertCircle} label="待审核输出" value={stats.pendingReviews} color="red" />
        <StatCard icon={FileText} label="最近更新 Prompt" value={stats.latestPromptUpdate?.name || '-'} subtext={formatDateShort(stats.latestPromptUpdate?.updatedAt)} color="primary" />
        <StatCard icon={BookOpen} label="最近更新知识库" value={stats.latestKnowledgeUpdate?.name || '-'} subtext={formatDateShort(stats.latestKnowledgeUpdate?.updatedAt)} color="primary" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Layers} label="工作流 Agent" value={stats.workflowAgents} subtext="预置 4 大工作流模板" color="purple" />
        <StatCard icon={Upload} label="知识库文件" value={stats.totalKbFiles} subtext={`待处理 ${stats.pendingFiles}`} color="emerald" />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-4">四大核心工作台</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <WorkbenchCard icon={ShoppingCart} title="Listing 优化台" desc="A10 / IDQ / Rufus / Cosmo / Alexa 优化" path="/workbench/listing" color="purple" />
          <WorkbenchCard icon={Megaphone} title="广告诊断台" desc="SP / SB / SD 广告诊断与动作表" path="/workbench/ads" color="orange" />
          <WorkbenchCard icon={Image} title="视觉 Prompt 台" desc="主图 / A+ / 场景图 Prompt 生成" path="/workbench/visual" color="pink" />
          <WorkbenchCard icon={HelpCircle} title="QA / Rufus 台" desc="Rufus / Cosmo / Alexa / 退货预防" path="/workbench/qa" color="cyan" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Agent 迁移状态分布</h3>
          <div className="space-y-3">
            {['未迁移','半迁移','迁移中','已API化','保留链接'].map(status => {
              const count = agents.filter(a => a.migrateStatus === status).length
              const pct = Math.round((count / agents.length) * 100)
              return (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">{status}</span>
                    <span className="text-slate-800 font-medium">{count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-primary-600 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="card p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Agent 类型分布</h3>
          <div className="space-y-3">
            {['single','workflow'].map(type => {
              const count = agents.filter(a => a.agentType === type).length
              const pct = Math.round((count / agents.length) * 100)
              return (
                <div key={type}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">{type === 'single' ? '单 Agent' : '工作流 Agent'}</span>
                    <span className="text-slate-800 font-medium">{count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
